import { ApiService } from "@core/api/api-service";
import { isApiError } from "@core/api/api-error";
import { Disease, diseases, FunctionalStatus } from "@domain/medical-condition/medical-condition";
import {
	Patient,
	PatientBase,
	PatientGlobalComplianceListItem,
	PatientNameListItem,
	PatientStatus,
	PendingPatient,
	RegisteredPatient,
} from "@domain/patient/patient";
import { PatientDatedProgramSummary } from "@domain/program/patient-program";
import { extractGlobalProgramStatusFromPatient } from "@domain/program/program-status/program-status-utils";
import { dateFromDateDto } from "@utils/date-utils";

interface PendingPatientStatDto extends Omit<PendingPatient, "creationDate" | "status"> {
	creationDate: string;
}

interface PatientDto extends Omit<Patient, "birthDate" | "creationDate"> {
	creationDate: string;
	birthDate?: string;
}

interface PatientDatedProgramSummaryDto {
	id: string;
	startDate: string;
	endDate: string;
}

interface RegisteredPatientsDto
	extends Omit<
		RegisteredPatient,
		"lastCompletedSessionDate" | "compliance" | "previousProgram" | "ongoingProgram" | "nextProgram"
	> {
	lastCompletedSessionDate?: string;
	progress?: number | null;
	previousProgram?: PatientDatedProgramSummaryDto;
	ongoingProgram?: PatientDatedProgramSummaryDto;
	nextProgram?: PatientDatedProgramSummaryDto;
}

export type PendingPatientCreateDto = Omit<PatientBase, "id" | "analyticsId">;

export class PatientService {
	constructor(private readonly apiService: ApiService) {}

	async createPendingPatient(pendingPatientCreateDto: PendingPatientCreateDto): Promise<PendingPatient> {
		const result = await this.apiService.post<PendingPatientStatDto>("/pending-patients", pendingPatientCreateDto);
		return PatientService.pendingPatientStatFromDto(result.data);
	}

	async retrievePatient(patientId: string): Promise<Patient> {
		const result = await this.apiService.get<PatientDto>(`/patients/${patientId}`);
		return PatientService.patientFromDto(result.data);
	}

	async deletePatient(patientId: string): Promise<void> {
		await this.apiService.delete<void>(`/patients/${patientId}`);
	}

	async editPatientDetails(
		patientId: string,
		disease?: Disease,
		functionalStatus?: FunctionalStatus,
	): Promise<void> {
		await this.apiService.post<void>(`/patients/${patientId}`, { disease, functionalStatus });
	}

	async deletePendingPatient(patientId: string): Promise<void> {
		await this.apiService.delete<void>(`/pending-patients/${patientId}`);
	}

	async listPendingPatients(): Promise<PendingPatient[]> {
		const result = await this.apiService.get<PendingPatientStatDto[]>("/pending-patients");
		return result.data.map(patient => PatientService.pendingPatientStatFromDto(patient));
	}

	async listRegisteredPatient(): Promise<RegisteredPatient[]> {
		const result = await this.apiService.get<RegisteredPatientsDto[]>("/patients/registered");
		return result.data.map(patient => PatientService.registeredPatientsFromDto(patient));
	}

	async listRegisteredPatientNames(): Promise<PatientNameListItem[]> {
		const result = await this.apiService.get<PatientNameListItem[]>("/patients/registered/names");
		return result.data;
	}

	async listRegisteredPatientGlobalCompliances(): Promise<PatientGlobalComplianceListItem[]> {
		const result = await this.apiService.get<PatientGlobalComplianceListItem[]>(
			"/patients/registered/global-compliances",
		);
		return result.data;
	}

	async sendInvitationCodeEmail(patientId: string, email: string): Promise<void> {
		await this.apiService.post<void>(
			`/pending-patients/${patientId}/send-code/email`,
			{ email },
			undefined,
			error => !(isApiError(error) && error.status === 400),
		);
	}

	private static pendingPatientStatFromDto(pendingPatientDto: PendingPatientStatDto): PendingPatient {
		return {
			...pendingPatientDto,
			status: PatientStatus.Pending,
			creationDate: dateFromDateDto(pendingPatientDto.creationDate),
		};
	}

	private static registeredPatientsFromDto(registeredPatientsDto: RegisteredPatientsDto): RegisteredPatient {
		const ongoingProgram = PatientService.PatientDatedProgramSummaryFromDto(registeredPatientsDto.ongoingProgram);
		const previousProgram = PatientService.PatientDatedProgramSummaryFromDto(registeredPatientsDto.previousProgram);
		const nextProgram = PatientService.PatientDatedProgramSummaryFromDto(registeredPatientsDto.nextProgram);
		return {
			...registeredPatientsDto,
			disease: diseases.includes(registeredPatientsDto.disease as Disease)
				? registeredPatientsDto.disease
				: Disease.OTHER,
			lastCompletedSessionDate: registeredPatientsDto.lastCompletedSessionDate
				? dateFromDateDto(registeredPatientsDto.lastCompletedSessionDate)
				: undefined,
			globalProgramStatus: extractGlobalProgramStatusFromPatient({ previousProgram, ongoingProgram, nextProgram }),
			compliance: undefined,
		};
	}

	private static patientFromDto(patientDto: PatientDto): Patient {
		return {
			...patientDto,
			creationDate: dateFromDateDto(patientDto.creationDate),
			birthDate: patientDto.birthDate ? dateFromDateDto(patientDto.birthDate) : undefined,
			disease: diseases.includes(patientDto.disease as Disease) ? patientDto.disease : Disease.OTHER,
		};
	}

	private static PatientDatedProgramSummaryFromDto(
		dto: PatientDatedProgramSummaryDto | undefined,
	): PatientDatedProgramSummary | undefined {
		return dto
			? {
					id: dto.id,
					startDate: dateFromDateDto(dto.startDate),
					endDate: dateFromDateDto(dto.endDate),
				}
			: undefined;
	}
}
